import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { orpc } from "@/frontend/orpc-client";

/**
 * Subscribes to the /api/export-events SSE endpoint and invalidates
 * export-related React Query caches on:
 *
 *   - `open`: initial connect and every auto-reconnect. Catches up on any
 *     terminal event the client may have missed while disconnected.
 *   - `export-item`: per-account tick during an object-storage run.
 *   - `export-progress`: per-layer resource-count tick during a FHIR-server
 *     run. `export_run.resources_posted` is committed before the SSE fires,
 *     so refetches see a consistent counter.
 *   - `export-run`: run-level state transition, including terminal.
 *
 * The DB is the source of truth; banners/history re-read from it after each
 * invalidation.
 *
 * The EventSource auto-reconnects on disconnect; the connection is closed
 * on unmount.
 */
export function useExportEvents(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const es = new EventSource("/api/export-events");

    const invalidate = (): void => {
      void queryClient.invalidateQueries({ queryKey: orpc.admin.listExportRuns.key() });
      void queryClient.invalidateQueries({ queryKey: orpc.admin.getActiveExportRun.key() });
      void queryClient.invalidateQueries({ queryKey: orpc.admin.listExportDestinations.key() });
    };

    es.addEventListener("open", invalidate);
    es.addEventListener("export-item", invalidate);
    es.addEventListener("export-progress", invalidate);
    es.addEventListener("export-run", invalidate);

    return () => {
      es.close();
    };
  }, [queryClient]);
}
